import React, { useMemo, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  ExpenseSubcategoryDoc,
  OTHER_REPAIR_CATEGORY_KEY,
  RepairCategoryDoc,
} from '@otw/models'
import { useRepairCategories } from '@/hooks/useRepairCategories'
import { useExpenseSubcategories } from '@/hooks/useExpenseSubcategories'
import { useRepairSubcategoryImpact } from '@/hooks/useRepairSubcategoryImpact'
import { getRepairCategoryIcon } from './repairCategoryIcons'
import RepairCategoryIconPicker from './RepairCategoryIconPicker'

interface RepairTaxonomyDialogProps {
  open: boolean
  onClose: () => void
}

/**
 * Single-screen tree view for the repair taxonomy. Categories are top-level rows
 * with inline expand to reveal nested subcategories. Add / edit / delete actions
 * sit inline on every row; impact-aware confirms protect against accidental
 * label rewrites across existing expenses.
 */
export const RepairTaxonomyDialog: React.FC<RepairTaxonomyDialogProps> = ({ open, onClose }) => {
  const { categories } = useRepairCategories()
  const { subcategories } = useExpenseSubcategories()

  const subsByCategory = useMemo(() => {
    const map = new Map<string, ExpenseSubcategoryDoc[]>()
    for (const sub of subcategories) {
      const key = sub.categoryKey ?? OTHER_REPAIR_CATEGORY_KEY
      const list = map.get(key) ?? []
      list.push(sub)
      map.set(key, list)
    }
    for (const list of map.values()) list.sort((a, b) => a.label.localeCompare(b.label))
    return map
  }, [subcategories])

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(categories.map(c => c.key)))
  const [addCategoryOpen, setAddCategoryOpen] = useState(false)

  const toggle = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        Manage repair taxonomy
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<AddIcon />} onClick={() => setAddCategoryOpen(true)}>
          Add category
        </Button>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1 }}>
        <Stack spacing={0.5}>
          {categories.map(category => (
            <CategoryNode
              key={category.id}
              category={category}
              subcategories={subsByCategory.get(category.key) ?? []}
              categories={categories}
              expanded={expanded.has(category.key)}
              onToggle={() => toggle(category.key)}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>

      {addCategoryOpen && (
        <AddCategoryDialog onClose={() => setAddCategoryOpen(false)} />
      )}
    </Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/*                              CATEGORY NODE                                 */
/* -------------------------------------------------------------------------- */

const CategoryNode: React.FC<{
  category: RepairCategoryDoc
  subcategories: ExpenseSubcategoryDoc[]
  categories: RepairCategoryDoc[]
  expanded: boolean
  onToggle: () => void
}> = ({ category, subcategories, categories, expanded, onToggle }) => {
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [addSubOpen, setAddSubOpen] = useState(false)
  const { Icon } = getRepairCategoryIcon(category.iconKey)

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          py: 0.75,
          px: 1,
          cursor: 'pointer',
          bgcolor: expanded ? 'action.hover' : 'background.paper',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={onToggle}
      >
        <IconButton size="small" sx={{ p: 0.25 }}>
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Icon fontSize="small" />
        <Typography variant="body2" sx={{ fontWeight: 600, ml: 0.5 }}>
          {category.label}
        </Typography>
        {category.isBuiltIn && (
          <Chip label="Built-in" size="small" variant="outlined" sx={{ height: 18, ml: 0.5 }} />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {subcategories.length} subcategor{subcategories.length === 1 ? 'y' : 'ies'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Add subcategory">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              setAddSubOpen(true)
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit category">
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              setEditOpen(true)
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            category.isBuiltIn
              ? 'Built-in categories cannot be deleted'
              : category.key === OTHER_REPAIR_CATEGORY_KEY
                ? 'The "Other" category cannot be deleted'
                : 'Delete category'
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={category.isBuiltIn || category.key === OTHER_REPAIR_CATEGORY_KEY}
              onClick={e => {
                e.stopPropagation()
                setDeleteOpen(true)
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Collapse in={expanded}>
        <Box sx={{ pl: 4, pr: 1, pb: 0.75 }}>
          {subcategories.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 0.5 }}>
              No subcategories yet.
            </Typography>
          )}
          {subcategories.length > 0 && (
            <Stack divider={<Divider flexItem />}>
              {subcategories.map(sub => (
                <SubcategoryNode key={sub.id} subcategory={sub} categories={categories} />
              ))}
            </Stack>
          )}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={e => {
              e.stopPropagation()
              setAddSubOpen(true)
            }}
            sx={{
              mt: 0.5,
              justifyContent: 'flex-start',
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: 1,
              border: '1px dashed',
              borderColor: 'divider',
              width: '100%',
              py: 0.5,
              '&:hover': { bgcolor: 'action.hover', borderColor: 'error.light', color: 'error.main' },
            }}
          >
            Add subcategory to {category.label}
          </Button>
        </Box>
      </Collapse>

      {editOpen && (
        <EditCategoryDialog
          target={category}
          onClose={() => setEditOpen(false)}
        />
      )}
      {deleteOpen && (
        <DeleteCategoryDialog
          target={category}
          categories={categories}
          subcategories={subcategories}
          onClose={() => setDeleteOpen(false)}
        />
      )}
      {addSubOpen && (
        <AddSubcategoryDialog
          categories={categories}
          presetCategoryKey={category.key}
          onClose={() => setAddSubOpen(false)}
        />
      )}
    </Box>
  )
}

const SubcategoryNode: React.FC<{
  subcategory: ExpenseSubcategoryDoc
  categories: RepairCategoryDoc[]
}> = ({ subcategory, categories }) => {
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ py: 0.5 }}>
      <Typography variant="body2">{subcategory.label}</Typography>
      {subcategory.isBuiltIn && (
        <Chip label="Built-in" size="small" variant="outlined" sx={{ height: 18, ml: 0.5 }} />
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Tooltip title="Edit subcategory">
        <IconButton size="small" onClick={() => setEditOpen(true)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={subcategory.isBuiltIn ? 'Built-in subcategory cannot be deleted' : 'Delete subcategory'}
      >
        <span>
          <IconButton
            size="small"
            disabled={subcategory.isBuiltIn}
            onClick={() => setDeleteOpen(true)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {editOpen && (
        <EditSubcategoryDialog
          target={subcategory}
          categories={categories}
          onClose={() => setEditOpen(false)}
        />
      )}
      {deleteOpen && (
        <DeleteSubcategoryDialog
          target={subcategory}
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </Stack>
  )
}

/* -------------------------------------------------------------------------- */
/*                              CATEGORY DIALOGS                              */
/* -------------------------------------------------------------------------- */

const AddCategoryDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { createCategory, isCreatingCategory } = useRepairCategories()
  const [label, setLabel] = useState('')
  const [iconKey, setIconKey] = useState<string | null>('build')

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add category</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Category name"
          value={label}
          onChange={e => setLabel(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Icon
        </Typography>
        <RepairCategoryIconPicker value={iconKey} onChange={setIconKey} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isCreatingCategory}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!label.trim() || isCreatingCategory}
          onClick={async () => {
            await createCategory({ label, iconKey })
            onClose()
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const EditCategoryDialog: React.FC<{
  target: RepairCategoryDoc
  onClose: () => void
}> = ({ target, onClose }) => {
  const { updateCategory, isUpdatingCategory } = useRepairCategories()
  const [label, setLabel] = useState(target.label)
  const [iconKey, setIconKey] = useState<string | null>(target.iconKey ?? null)

  const dirty = label.trim() !== target.label || iconKey !== (target.iconKey ?? null)

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit category</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Renaming a category only changes its display label. Subcategories and expenses are not
          touched.
        </Alert>
        <TextField
          autoFocus
          fullWidth
          label="Category name"
          value={label}
          onChange={e => setLabel(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Icon
        </Typography>
        <RepairCategoryIconPicker value={iconKey} onChange={setIconKey} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUpdatingCategory}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!dirty || !label.trim() || isUpdatingCategory}
          onClick={async () => {
            await updateCategory({
              id: target.id,
              label: label.trim() !== target.label ? label : undefined,
              iconKey: iconKey !== (target.iconKey ?? null) ? iconKey : undefined,
            })
            onClose()
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const DeleteCategoryDialog: React.FC<{
  target: RepairCategoryDoc
  categories: RepairCategoryDoc[]
  subcategories: ExpenseSubcategoryDoc[]
  onClose: () => void
}> = ({ target, categories, subcategories, onClose }) => {
  const { deleteCategory, isDeletingCategory } = useRepairCategories()
  const { updateSubcategory } = useExpenseSubcategories()
  const [reassignTo, setReassignTo] = useState<string>(OTHER_REPAIR_CATEGORY_KEY)
  const [busy, setBusy] = useState(false)
  const reassignNeeded = subcategories.length > 0
  const otherCategories = categories.filter(c => c.key !== target.key)

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete category</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Delete <strong>{target.label}</strong>?
        </Typography>
        {reassignNeeded ? (
          <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>
                {subcategories.length} subcategor{subcategories.length === 1 ? 'y is' : 'ies are'}{' '}
                still in this category
              </AlertTitle>
              They will be moved to the chosen category. Expense data is not touched.
            </Alert>
            <FormControl fullWidth size="small">
              <InputLabel>Move all to</InputLabel>
              <Select
                label="Move all to"
                value={reassignTo}
                onChange={e => setReassignTo(e.target.value as string)}
              >
                {otherCategories.map(c => (
                  <MenuItem key={c.key} value={c.key}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        ) : (
          <Alert severity="info">No subcategories reference this category.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeletingCategory || busy}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={isDeletingCategory || busy}
          onClick={async () => {
            setBusy(true)
            try {
              if (reassignNeeded) {
                for (const sub of subcategories) {
                  await updateSubcategory({ id: sub.id, categoryKey: reassignTo })
                }
              }
              await deleteCategory(target.id)
              onClose()
            } finally {
              setBusy(false)
            }
          }}
        >
          {reassignNeeded ? 'Reassign & delete' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/*                             SUBCATEGORY DIALOGS                            */
/* -------------------------------------------------------------------------- */

const AddSubcategoryDialog: React.FC<{
  categories: RepairCategoryDoc[]
  presetCategoryKey?: string
  onClose: () => void
}> = ({ categories, presetCategoryKey, onClose }) => {
  const { createSubcategory, isCreatingSubcategory } = useExpenseSubcategories()
  const [label, setLabel] = useState('')
  const [categoryKey, setCategoryKey] = useState<string>(
    presetCategoryKey ?? categories[0]?.key ?? OTHER_REPAIR_CATEGORY_KEY,
  )

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add subcategory</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Subcategory name"
          value={label}
          onChange={e => setLabel(e.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <FormControl fullWidth size="small">
          <InputLabel>Parent category</InputLabel>
          <Select
            label="Parent category"
            value={categoryKey}
            onChange={e => setCategoryKey(e.target.value as string)}
          >
            {categories.map(c => (
              <MenuItem key={c.key} value={c.key}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isCreatingSubcategory}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!label.trim() || isCreatingSubcategory}
          onClick={async () => {
            await createSubcategory({ label, categoryKey })
            onClose()
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const EditSubcategoryDialog: React.FC<{
  target: ExpenseSubcategoryDoc
  categories: RepairCategoryDoc[]
  onClose: () => void
}> = ({ target, categories, onClose }) => {
  const { updateSubcategory, isUpdatingSubcategory } = useExpenseSubcategories()
  const [label, setLabel] = useState(target.label)
  const [categoryKey, setCategoryKey] = useState<string>(
    target.categoryKey ?? OTHER_REPAIR_CATEGORY_KEY,
  )
  const isRename = label.trim() !== target.label && label.trim().length > 0
  const impact = useRepairSubcategoryImpact(isRename ? target.label : null)

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit subcategory</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label="Subcategory name"
          value={label}
          onChange={e => setLabel(e.target.value)}
          disabled={target.isBuiltIn}
          helperText={target.isBuiltIn ? 'Built-in subcategories cannot be renamed.' : undefined}
          sx={{ mt: 1, mb: 2 }}
        />
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Parent category</InputLabel>
          <Select
            label="Parent category"
            value={categoryKey}
            onChange={e => setCategoryKey(e.target.value as string)}
          >
            {categories.map(c => (
              <MenuItem key={c.key} value={c.key}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isRename && (
          <Alert severity="warning">
            <AlertTitle>Renaming will update expense records</AlertTitle>
            {impact.isLoading ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CircularProgress size={14} />
                <Typography variant="body2">Checking impact…</Typography>
              </Stack>
            ) : impact.data ? (
              <Typography variant="body2">
                {impact.data.expenseCount} expense{impact.data.expenseCount === 1 ? '' : 's'} and{' '}
                {impact.data.lineItemMatches} line item
                {impact.data.lineItemMatches === 1 ? '' : 's'} reference{' '}
                <strong>{target.label}</strong>. They will be rewritten to{' '}
                <strong>{label.trim()}</strong>.
              </Typography>
            ) : null}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUpdatingSubcategory}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            isUpdatingSubcategory ||
            (label.trim() === target.label &&
              categoryKey === (target.categoryKey ?? OTHER_REPAIR_CATEGORY_KEY))
          }
          onClick={async () => {
            await updateSubcategory({
              id: target.id,
              label: target.isBuiltIn ? undefined : label,
              categoryKey,
            })
            onClose()
          }}
        >
          {isRename ? `Rename & update ${impact.data?.expenseCount ?? 0}` : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const DeleteSubcategoryDialog: React.FC<{
  target: ExpenseSubcategoryDoc
  onClose: () => void
}> = ({ target, onClose }) => {
  const { deleteSubcategory, isDeletingSubcategory, subcategories } = useExpenseSubcategories()
  const impact = useRepairSubcategoryImpact(target.label)
  const [replacementLabel, setReplacementLabel] = useState<string>('')

  const replacements = subcategories.filter(s => s.label !== target.label)
  const replacementsInSameCategory = replacements
    .filter(
      s =>
        (s.categoryKey ?? OTHER_REPAIR_CATEGORY_KEY) ===
        (target.categoryKey ?? OTHER_REPAIR_CATEGORY_KEY),
    )
    .sort((a, b) => a.label.localeCompare(b.label))
  const otherReplacements = replacements
    .filter(
      s =>
        (s.categoryKey ?? OTHER_REPAIR_CATEGORY_KEY) !==
        (target.categoryKey ?? OTHER_REPAIR_CATEGORY_KEY),
    )
    .sort((a, b) => a.label.localeCompare(b.label))

  const needsReplacement = (impact.data?.expenseCount ?? 0) > 0
  const disabled = isDeletingSubcategory || (needsReplacement && !replacementLabel)

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete subcategory</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Delete <strong>{target.label}</strong>?
        </Typography>
        {impact.isLoading ? (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <CircularProgress size={14} />
            <Typography variant="body2">Checking impact…</Typography>
          </Stack>
        ) : impact.data ? (
          impact.data.expenseCount > 0 ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>
                  {impact.data.expenseCount} expense
                  {impact.data.expenseCount === 1 ? '' : 's'} and {impact.data.lineItemMatches} line
                  item{impact.data.lineItemMatches === 1 ? '' : 's'} reference this subcategory
                </AlertTitle>
                Pick a replacement — impacted expenses will be rewritten to the replacement before
                this subcategory is removed.
              </Alert>
              <FormControl fullWidth size="small">
                <InputLabel>Replacement subcategory</InputLabel>
                <Select
                  label="Replacement subcategory"
                  value={replacementLabel}
                  onChange={e => setReplacementLabel(e.target.value as string)}
                >
                  {replacementsInSameCategory.length > 0 && (
                    <ListSubheader>Same category</ListSubheader>
                  )}
                  {replacementsInSameCategory.map(s => (
                    <MenuItem key={s.id} value={s.label}>
                      {s.label}
                    </MenuItem>
                  ))}
                  {otherReplacements.length > 0 && (
                    <ListSubheader>Other categories</ListSubheader>
                  )}
                  {otherReplacements.map(s => (
                    <MenuItem key={s.id} value={s.label}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          ) : (
            <Alert severity="info">No expenses reference this subcategory.</Alert>
          )
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeletingSubcategory}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={disabled}
          onClick={async () => {
            await deleteSubcategory({
              id: target.id,
              replacementLabel: needsReplacement ? replacementLabel : null,
            })
            onClose()
          }}
        >
          {needsReplacement
            ? `Reassign ${impact.data?.expenseCount ?? 0} & delete`
            : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RepairTaxonomyDialog
